import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlatformManagerService } from "@/lib/platformManager/platformManager.service";
import type { PlatformStats } from "@/lib/platformManager/platformManager.service";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import {
	Users,
	Coins,
	TrendingUp,
	Activity,
	MessageSquare,
	CreditCard,
	ArrowRight,
	Radio,
	BarChart3,
} from "lucide-react";

interface NavigationCard {
	title: string;
	description: string;
	icon: React.ReactNode;
	path: string;
	color: string;
}

export default function PlatformManagerDashboardPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [stats, setStats] = useState<PlatformStats | null>(null);
	const [loading, setLoading] = useState(true);

	const platformManagerService = PlatformManagerService.getInstance();

	// Load platform stats
	useEffect(() => {
		const loadStats = async () => {
			try {
				setLoading(true);
				const statsData = await platformManagerService.getPlatformStats();
				setStats(statsData);
			} catch (error) {
				console.error("Failed to load platform stats:", error);
				toast.error(t("platformManager.errors.loadFailed"));
			} finally {
				setLoading(false);
			}
		};

		loadStats();
	}, [platformManagerService, t]);

	// Navigation cards configuration
	const navigationCards: NavigationCard[] = [
		{
			title: t("platformManager.navigation.userManagement"),
			description: t("platformManager.navigation.userManagementDesc"),
			icon: <Users className="h-6 w-6" />,
			path: "/platform-manager/users",
			color: "bg-blue-500",
		},
		{
			title: t("platformManager.navigation.pipelineMonitoring"),
			description: t("platformManager.navigation.pipelineMonitoringDesc"),
			icon: <Radio className="h-6 w-6" />,
			path: "/platform-manager/pipeline",
			color: "bg-purple-500",
		},
		{
			title: t("platformManager.navigation.subscriptions"),
			description: t("platformManager.navigation.subscriptionsDesc"),
			icon: <CreditCard className="h-6 w-6" />,
			path: "/platform-manager/subscriptions",
			color: "bg-green-500",
		},
		{
			title: t("platformManager.navigation.feedback"),
			description: t("platformManager.navigation.feedbackDesc"),
			icon: <MessageSquare className="h-6 w-6" />,
			path: "/platform-manager/feedback",
			color: "bg-orange-500",
		},
		{
			title: t("platformManager.navigation.analytics"),
			description: t("platformManager.navigation.analyticsDesc"),
			icon: <BarChart3 className="h-6 w-6" />,
			path: "/platform-manager/analytics",
			color: "bg-indigo-500",
		},
	];

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto" />
					<p className="mt-4 text-gray-600">
						{t("platformManager.errors.loadingData")}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto p-6 space-y-8">
			{/* Header */}
			<div>
				<h1 className="text-3xl font-bold">
					{t("platformManager.dashboard.title")}
				</h1>
				<p className="text-gray-600">
					{t("platformManager.dashboard.description")}
				</p>
			</div>

			{/* Statistics Overview */}
			{stats && (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								{t("platformManager.stats.totalUsers")}
							</CardTitle>
							<Users className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">{stats.totalUsers}</div>
							<p className="text-xs text-muted-foreground">
								{stats.activeUsersLast7Days}{" "}
								{t("platformManager.stats.activeUsers")}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								{t("platformManager.stats.totalCoins")}
							</CardTitle>
							<Coins className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{stats.totalCoinsInCirculation.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">
								{t("platformManager.stats.avgPerUser", {
									average: stats.averageBalancePerUser,
								})}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								{t("platformManager.stats.transactions")}
							</CardTitle>
							<TrendingUp className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{stats.totalTransactions.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">
								{t("platformManager.stats.allTime")}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								{t("platformManager.stats.podcasts")}
							</CardTitle>
							<Activity className="h-4 w-4 text-muted-foreground" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{stats.totalPodcasts.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">
								{t("platformManager.stats.created")}
							</p>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Navigation Cards */}
			<div>
				<h2 className="text-xl font-semibold mb-4">
					{t("platformManager.dashboard.quickAccess")}
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{navigationCards.map((card) => (
						<Card
							key={card.path}
							className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
							onClick={() => navigate(card.path)}
						>
							<CardHeader>
								<div className="flex items-start justify-between">
									<div className={`p-3 rounded-lg ${card.color} text-white`}>
										{card.icon}
									</div>
									<ArrowRight className="h-5 w-5 text-gray-400" />
								</div>
								<CardTitle className="mt-4">{card.title}</CardTitle>
								<CardDescription>{card.description}</CardDescription>
							</CardHeader>
						</Card>
					))}
				</div>
			</div>
		</div>
	);
}
